import { Injectable, inject } from '@angular/core';
import { LanguageCode, LanguageIso } from '@mkp/shared/models';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable, take } from 'rxjs';
import { CookieService } from '@mkp/shared/util-device';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly cookieService = inject(CookieService);
  private readonly translateService = inject(TranslateService);

  private readonly defaultLanguage = LanguageIso.ENGLISH;

  initAppLanguageForUser(language: LanguageCode): Observable<{ languageIso: LanguageIso }> {
    const languageIso = this.toLanguageIso(language);
    return this.initAppLanguage(languageIso);
  }

  initAppLanguageForVisitor(): Observable<{ languageIso: LanguageIso }> {
    const languageIso = this.getVisitorLanguageIso();
    return this.initAppLanguage(languageIso);
  }

  switchAppLanguage(languageIso: LanguageIso): void {
    this.setHtmlLangAttribute(languageIso);
    this.translateService.use(languageIso);
    this.cookieService.setCookieString('language', languageIso);
  }

  toLanguageIso(language: string): LanguageIso {
    const lang = language ? language.substr(0, 2) : this.defaultLanguage.substr(0, 2);
    const test = String(lang).match(/^en|fr|de$/gi);
    return (test ? `${lang}-CH` : this.defaultLanguage) as LanguageIso;
  }

  private initAppLanguage(languageIso: LanguageIso): Observable<{ languageIso: LanguageIso }> {
    this.switchAppLanguage(languageIso);
    return this.translateService.onLangChange.pipe(
      map(() => ({ languageIso })),
      take(1)
    );
  }

  private getVisitorLanguageIso(): LanguageIso {
    return (
      (this.cookieService.getCookieString('language') as LanguageIso) ??
      this.toLanguageIso(window.navigator.language)
    );
  }

  private setHtmlLangAttribute(languageIso: LanguageIso): void {
    document.documentElement.lang = languageIso;
  }
}
